import { Chevron } from "@components/icons"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { body16BoldStyles, body16MainStyles } from "@constants/typography"
import { mapSortMethodToName } from "@utils/mapSortMethodToName"
import { SortMethods } from "@views/hooks/useCollectionSorting"
import React, { useState } from "react"
import styled from "styled-components"

const DropdownIcon = styled.div`
  width: 24px;
  height: 24px;
  transition: transform ${animation.medium} ${cubicBezier};
  transform: rotate(90deg);
  z-index: 5;
`

const DropdownDisplayValue = styled.div`
  padding: 0 10px;
  z-index: 5;
`

const SortBy = styled.div`
  transition: transform ${animation.medium} ${cubicBezier};
  transform: translateX(0);
  z-index: 5;
`

const SortHeader = styled.div`
  ${body16MainStyles}
  z-index: 5;
  display: flex;
  position: relative;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  background: none;
  width: 304px;
  min-height: 56px;
  transition: transform ${animation.medium} ${cubicBezier};
  transform-origin: top left;
  padding: 0 20px;

  :before {
    content: "";
    transition: opacity ${animation.medium} ${cubicBezier};
    transform-origin: top left;
    background-color: ${colors.black};
    z-index: 5;
    position: absolute;
    top: 0;
    left: 0;
    width: 304px;
    height: 56px;
    opacity: 0;
  }

  @media ${breakpoints.laptop} {
    &:hover {
      cursor: pointer;
    }
  }
`

const SelectOption = styled.div`
  ${body16MainStyles}
  color: ${colors.offWhite};
  cursor: pointer;
  position: relative;
  padding: 18px 0 18px 20px;
  z-index: 5;
  border-top: solid 1px rgba(0, 0, 0, 0.9);

  :after {
    content: "";
    z-index: -1;
    position: absolute;
    top: 0;
    left: 0;
    background-color: ${colors.black};
    width: 100%;
    height: 56px;
  }

  @media ${breakpoints.laptop} {
    &:hover {
      &::before {
        width: 1px;
        transform: scaleX(8);
        transition:
          background-color 0 linear 50ms,
          transform ${animation.medium} ${cubicBezier};
      }

      span {
        transform: translateX(24px);
        transition: transform ${animation.medium} ${cubicBezier};
      }
    }
  }

  &::before {
    z-index: 5;
    position: absolute;
    top: 27px;
    left: 19px;
    width: 0px;
    height: 1px;
    background-color: ${colors.offWhite};
    content: "";
    transition:
      background-color ${animation.medium} ${cubicBezier} 50ms,
      transform ${animation.medium} ${cubicBezier} 50ms;
    transform-origin: left;
  }

  span {
    z-index: 5;
    display: flex;
    transition: transform ${animation.medium} ${cubicBezier};
  }

  &.selected {
    ${body16BoldStyles}
    color: ${colors.offWhite};
    &::after {
      background-color: ${colors.offBlack};
    }
  }
`

const DropShadow = styled.div`
  background: ${colors.black};
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.25);
  opacity: 0;
  transition: opacity ${animation.medium} ${cubicBezier};
`

export const SortContainer = styled.div`
  display: none;
  width: 304px;
  height: 56px;
  overflow: hidden;
  z-index: 5;

  @media ${breakpoints.tablet} {
    display: flex;
    flex-direction: column;

    :active,
    :focus {
      outline: none;
      overflow: visible;
      ${DropShadow} {
        opacity: 1;
      }
      ${SortHeader} {
        color: ${colors.offWhite};
        width: 304px;
        :before {
          opacity: 1;
        }
        ${DropdownIcon} {
          transform: rotate(-90deg);
        }
        ${DropdownDisplayValue} {
          display: none;
        }
        ${SortBy} {
          transform: translateX(-180px);
        }
        > div > svg {
          stroke: ${colors.offWhite};
        }
      }
    }
  }

  @media ${breakpoints.laptop} {
    display: flex;
    flex-direction: column;

    :active,
    :focus {
      outline: none;
      overflow: visible;
      ${DropShadow} {
        opacity: 1;
      }
      ${SortHeader} {
        color: ${colors.offWhite};
        width: 304px;
        :before {
          opacity: 1;
        }
        ${DropdownIcon} {
          transform: rotate(-90deg);
        }
        ${DropdownDisplayValue} {
          display: none;
        }
        ${SortBy} {
          transform: translateX(-180px);
        }
        > div > svg {
          stroke: ${colors.offWhite};
        }
      }
    }
  }
`

interface SortDropdownProps {
  isCustomCollection: boolean
  options: Array<{ name: string; sort: string }>
  setSort: (string) => void
  sortMethod?: SortMethods
}

const SortDropdown: React.FC<SortDropdownProps> = ({
  isCustomCollection,
  options,
  setSort,
  sortMethod,
}) => {
  const sortName = mapSortMethodToName(sortMethod) ?? "Newest"
  const [value, setValue] = useState(isCustomCollection ? "Featured" : sortName)

  return (
    <SortContainer id={"SortContainer"} tabIndex={0}>
      <SortHeader>
        <SortBy>{`Sort by: `}</SortBy>
        <DropdownDisplayValue>{value}</DropdownDisplayValue>
        <DropdownIcon>
          <Chevron />
        </DropdownIcon>
      </SortHeader>
      <DropShadow>
        {options.map((item: { name: string; sort: string }) => (
          <SelectOption
            className={item.name === value ? "selected" : ""}
            key={item.name}
            onClick={() => {
              setValue(item.name)
              setSort(item.sort)
              if (typeof document !== "undefined") {
                document.getElementById("SortContainer")?.blur()
              }
            }}
          >
            <span>{item.name}</span>
          </SelectOption>
        ))}
      </DropShadow>
    </SortContainer>
  )
}

export default SortDropdown
